<section class="min-h-full space-y-5 bg-base-200/25 p-4 sm:p-6" role="region" aria-label="Store Products Inventory">
  <header class="space-y-5">
    <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
      <div>
        <h1 class="text-2xl font-black tracking-tight" id="page-title">Store products</h1>
        <p class="mt-1 text-sm text-base-content/55">Choose a store, then manage its product variants, price and stock.</p>
      </div>
      <div class="flex gap-2">
        <button (click)="refreshInventory()" class="btn btn-sm btn-ghost rounded-xl"><i class="fa-solid fa-rotate-right"></i> Refresh</button>
        <button routerLink="/inventory/store-products/add" class="btn btn-sm btn-primary rounded-xl"><i class="fa-solid fa-plus"></i> Assign products</button>
      </div>
    </div>

    <div class="grid grid-cols-2 gap-3 lg:grid-cols-4">
      <div class="rounded-2xl border border-base-200 bg-base-100 p-4"><p class="text-xs font-bold text-base-content/45">Products</p><p class="mt-1 text-2xl font-black">{{ totalProducts() }}</p></div>
      <div class="rounded-2xl border border-base-200 bg-base-100 p-4"><p class="text-xs font-bold text-base-content/45">Variants on page</p><p class="mt-1 text-2xl font-black">{{ totalVariants() }}</p></div>
      <div class="rounded-2xl border border-success/25 bg-success/5 p-4"><p class="text-xs font-bold text-base-content/45">In stock</p><p class="mt-1 text-2xl font-black text-success">{{ liveVariants() }}</p></div>
      <div class="rounded-2xl border border-warning/25 bg-warning/5 p-4"><p class="text-xs font-bold text-base-content/45">Needs attention</p><p class="mt-1 text-2xl font-black text-warning">{{ lowStockVariants() + outOfStockVariants() }}</p></div>
    </div>

    <div class="rounded-2xl border border-base-200 bg-base-100 p-4 shadow-sm">
      <div class="grid gap-3 md:grid-cols-2 xl:grid-cols-[220px_minmax(220px,1fr)_minmax(260px,1fr)]">
        <select
          class="select select-sm select-bordered w-full rounded-xl"
          [ngModel]="selectedStoreId()"
          (ngModelChange)="selectedStoreId.set($event); onStoreChange()"
          [disabled]="authService.role() === 'store_manager'"
        >
          @if (authService.role() !== 'store_manager') {
            <option [ngValue]="null">All mapped stores</option>
          }
          @for (store of stores(); track store.id) {
            <option [ngValue]="store.id">{{ store.name }}</option>
          }
        </select>

        <app-category-filter [value]="categoryFilter().id" (selectionChange)="setCategory($event)" />

        <label class="input input-sm input-bordered flex items-center gap-2 rounded-xl">
          <i class="fa-solid fa-search text-base-content/40"></i>
          <input
            type="text"
            class="grow"
            placeholder="Search products or SKUs..."
            [ngModel]="searchText()"
            (ngModelChange)="searchText.set($event); onSearchChange()"
          />
          @if (searchText()) {
            <button (click)="searchText.set(''); onSearchChange()" class="btn btn-ghost btn-xs btn-circle"><i class="fa-solid fa-xmark"></i></button>
          }
        </label>

      </div>
      <div class="mt-3 flex gap-2 overflow-x-auto pb-1">
          @for (filter of quickFilters(); track filter.value) {
            <button
              (click)="setQuickFilter(filter.value)"
              class="btn btn-xs shrink-0 rounded-lg"
              [class.btn-primary]="availability() === filter.value"
              [class.btn-ghost]="availability() !== filter.value"
            >
              {{ filter.label }} <span class="opacity-60">{{ filter.count }}</span>
            </button>
          }
      </div>
    </div>
  </header>

  <!-- Main Content -->
  <main>
    @if (loading()) {
      <div class="space-y-3">
        @for (item of [1,2,3,4,5]; track item) {
          <div class="h-16 bg-base-100 rounded-xl border border-base-200 animate-pulse"></div>
        }
      </div>
    } @else if (error()) {
      <div class="alert alert-error shadow-sm rounded-xl max-w-3xl mx-auto mt-8">
        <i class="fa-solid fa-circle-exclamation text-xl"></i>
        <div>
          <h4 class="font-bold">Error Loading Products</h4>
          <p class="text-sm">{{ error() }}</p>
        </div>
        <button (click)="refreshInventory()" class="btn btn-sm btn-ghost">Retry</button>
      </div>
    } @else if (filteredProducts().length === 0) {
      <div class="flex flex-col items-center justify-center py-20 text-center bg-base-100 rounded-2xl border border-base-200 mt-8">
        <div class="h-16 w-16 bg-base-200 rounded-full flex items-center justify-center mb-4 text-2xl text-base-content/30">
          <i class="fa-solid fa-box-open"></i>
        </div>
        <h3 class="text-lg font-bold">No products found</h3>
        <p class="text-sm text-base-content/50 max-w-sm mt-1">Try adjusting your search criteria or switch to another store.</p>
        <button (click)="searchText.set(''); availability.set('all'); categoryFilter.set({ id: null, ids: [] }); loadStoreProducts()" class="btn btn-sm btn-outline mt-5">Clear Filters</button>
      </div>
    } @else {
      <!-- Data List (Accordion Style) -->
      <div class="space-y-3">
        @for (product of paginatedProducts(); track trackByProductId($index, product)) {
          <div class="overflow-hidden rounded-2xl border border-base-200 bg-base-100 shadow-sm" [class.border-primary]="isExpanded(product.groupKey)">
            <!-- Summary Row (Clickable) -->
            <button 
              (click)="toggleExpandProduct(product.groupKey)"
              class="flex w-full items-center justify-between gap-4 p-4 text-left hover:bg-base-200/25 focus:outline-none"
            >
              <div class="flex items-center gap-4 flex-1 min-w-0">
                <div class="h-12 w-12 rounded-lg border border-base-200 overflow-hidden bg-base-200 shrink-0">
                  <img [src]="product.image" [alt]="product.name" class="w-full h-full object-cover">
                </div>
                <div class="flex-1 min-w-0 pr-4">
                  <h3 class="truncate text-sm font-bold">{{ product.name }}</h3>
                  <div class="flex items-center gap-3 mt-1.5">
                    <span class="badge badge-ghost badge-sm normal-case">{{ product.category }}</span>
                    <span class="text-xs text-base-content/50">{{ product.variants.length }} variants ? {{ product.storeName }}</span>
                  </div>
                </div>
              </div>
              <div class="flex items-center gap-4 shrink-0">
                 <div class="hidden text-right sm:block">
                    <p class="text-lg font-black">{{ productStock(product) }}</p>
                    <p class="text-[10px] font-bold uppercase text-base-content/40">units in stock</p>
                 </div>
                 <span class="btn btn-xs btn-ghost rounded-lg">{{ isExpanded(product.groupKey) ? 'Hide' : 'Manage' }} <i class="fa-solid" [class.fa-chevron-up]="isExpanded(product.groupKey)" [class.fa-chevron-down]="!isExpanded(product.groupKey)"></i></span>
              </div>
            </button>

            <!-- Expanded Details panel (Nested Variants Table) -->
            @if (isExpanded(product.groupKey)) {
              <div class="border-t border-base-200 bg-base-200/20 p-3 sm:p-4">
                @if (authService.isSuperAdmin()) {
                  <app-catalog-merchandising [storeId]="product.storeId" [productId]="product.id" />
                }
                <div class="overflow-x-auto rounded-lg border border-base-200 bg-base-100 shadow-sm">
                  <table class="table table-sm w-full">
                    <thead class="bg-base-200/50 text-xs uppercase tracking-wider text-base-content/70">
                      <tr>
                        <th class="font-semibold py-3 px-4 rounded-tl-lg">Variant SKU</th>
                        <th class="font-semibold py-3 px-4">Selling Price</th>
                        <th class="font-semibold py-3 px-4">Status & Stock</th>
                        <th class="font-semibold py-3 px-4 text-right rounded-tr-lg">Quick Actions</th>
                      </tr>
                    </thead>
                    <tbody class="text-sm">
                      @for (variant of product.variants; track trackByVariantId($index, variant)) {
                        <tr class="hover border-t border-base-200">
                          <td class="font-medium px-4 py-3">{{ variant.name }}
                            @if (variant.maxOrderQuantity != null) {
                              <span class="block text-xs text-base-content/60">Max {{ variant.maxOrderQuantity }} per order</span>
                            }
                          </td>
                          <td class="px-4 py-3 text-base-content/80 font-medium">
                            {{ variant.price | currency : 'INR' : 'symbol' : '1.0-2' }}
                            <span class="text-[10px] text-base-content/40 line-through ml-1">{{ variant.mrp | currency : 'INR' : 'symbol' : '1.0-2' }}</span>
                          </td>
                          <td class="px-4 py-3">
                             <div class="flex items-center gap-2">
                                <span class="badge badge-sm border-0 gap-1 font-semibold justify-start"
                                  [class.bg-success/10]="variant.status === 'IN STOCK'" [class.text-success]="variant.status === 'IN STOCK'"
                                  [class.bg-warning/10]="variant.status === 'LOW STOCK'" [class.text-warning]="variant.status === 'LOW STOCK'"
                                  [class.bg-error/10]="variant.status === 'OUT OF STOCK'" [class.text-error]="variant.status === 'OUT OF STOCK'"
                                  [class.bg-base-200]="variant.status === 'UNAVAILABLE'"
                                >
                                  {{ variant.status }}
                                </span>
                                <span class="text-xs font-mono bg-base-200 px-1.5 py-0.5 rounded text-base-content/70">{{ variant.stock }} u</span>
                             </div>
                          </td>
                          <td class="px-4 py-2 text-right">
                            <div class="flex items-center justify-end gap-1">
                              <button type="button" class="btn btn-xs btn-outline btn-primary" (click)="openVariantEditor(product, variant)" [disabled]="!selectedStoreId()">Edit settings</button>
                              <button class="btn btn-xs btn-outline btn-primary gap-1" (click)="openStockEditor(product, variant)">
                                <i class="fa-solid fa-box-open text-[10px]"></i> Update stock
                              </button>
                              <div class="dropdown dropdown-end">
                                <button tabindex="0" class="btn btn-xs btn-ghost btn-square">
                                  <i class="fa-solid fa-ellipsis-vertical"></i>
                                </button>
                                <ul tabindex="0" class="dropdown-content menu menu-sm bg-base-100 rounded-box z-10 w-36 shadow-lg border border-base-200 mt-1">
                                  <li><button type="button" (click)="openVariantEditor(product, variant)"><i class="fa-solid fa-pen text-base-content/60"></i> Edit details</button></li>
                                  @if (authService.isSuperAdmin()) {
                                    <li><button type="button" (click)="requestRemoveVariantMapping(product, variant)" class="text-error"><i class="fa-solid fa-trash"></i> Remove</button></li>
                                  }
                                </ul>
                              </div>
                            </div>
                          </td>
                        </tr>
                      }
                    </tbody>
                  </table>
                </div>
              </div>
            }
          </div>
        }
      </div>

      <div class="mt-5 overflow-hidden rounded-2xl border border-base-200 bg-base-100 shadow-sm">
        <app-admin-paginator [totalItems]="totalProducts()" [currentPage]="currentPage()" [pageSize]="pageSize()" [pageSizeOptions]="[10, 25, 50]" (pageChange)="setPage($event)" (pageSizeChange)="setPageSize($event)" />
      </div>
    }
  </main>

  <!-- Modals Section (unchanged visually but functionally mapped) -->
  @if (stockEditDraft(); as draft) {
    <div class="modal modal-open bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div class="modal-box rounded-2xl p-6 shadow-xl max-w-sm">
        <div class="flex items-center justify-between">
          <h3 class="text-lg font-black tracking-tight">Refill Stock</h3>
          <button type="button" class="btn btn-ghost btn-xs btn-circle bg-base-200" (click)="closeStockEditor()" [disabled]="stockSaving()">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <p class="mt-1 text-xs text-base-content/50 break-words font-medium">
          {{ draft.productName }} · {{ draft.variantName }}
        </p>

        <div class="my-5 rounded-lg border border-base-200 bg-base-50 p-4 text-center">
            <div class="text-[10px] font-bold uppercase tracking-wider text-base-content/40">Current Stock</div>
            <div class="text-4xl font-black mt-1">{{ draft.currentStock }}</div>
        </div>

        <div class="space-y-4">
          <app-stock-reason [disabled]="stockSaving()" (reasonChange)="stockChangeReason = $event" />
          <div class="flex gap-2">
            <div class="w-1/3">
              <div class="label py-1"><span class="label-text text-xs font-bold text-base-content/60">Mode</span></div>
              <select class="select select-sm select-bordered w-full font-medium" [ngModel]="draft.mode" (ngModelChange)="setStockEditMode($event)">
                <option value="add">+ Add</option>
                <option value="set">= Set</option>
              </select>
            </div>
            <div class="flex-1">
              <div class="label py-1"><span class="label-text text-xs font-bold text-base-content/60">Quantity Change</span></div>
              <input type="number" min="0" class="input input-sm input-bordered w-full font-bold text-lg" [ngModel]="draft.quantity" (ngModelChange)="setStockEditQuantity($event)"/>
            </div>
          </div>
          
          <div class="flex items-center justify-between pt-2 border-t border-base-200">
             <span class="text-xs font-semibold text-base-content/50">Resulting Stock:</span>
             <span class="text-lg font-black text-primary">
                @if (draft.mode === 'set') { {{ draft.quantity || 0 }} } @else { {{ draft.currentStock + (draft.quantity || 0) }} }
             </span>
          </div>
          <div class="label py-0"><span class="label-text text-xs font-bold text-base-content/60">Low stock threshold</span></div>
          <input type="number" min="0" class="input input-sm input-bordered w-full" [ngModel]="draft.lowStockThreshold" (ngModelChange)="setStockEditThreshold($event)"/>
          <label class="cursor-pointer flex items-center justify-between p-3 rounded-lg border border-base-200">
            <span class="text-sm font-bold">Available in store</span>
            <input type="checkbox" class="toggle toggle-sm toggle-success" [ngModel]="draft.isAvailable" (ngModelChange)="setStockEditAvailable($event)"/>
          </label>
        </div>

        <div class="mt-6 flex gap-2">
          <button class="btn btn-sm btn-ghost flex-1 border border-base-200" (click)="closeStockEditor()" [disabled]="stockSaving()">Cancel</button>
          <button class="btn btn-sm btn-primary flex-1 shadow-sm" (click)="saveStockEdit()" [disabled]="stockSaving()">
            @if (stockSaving()) { <span class="loading loading-spinner loading-xs"></span> } Save Stock
          </button>
        </div>
      </div>
      <button class="modal-backdrop" (click)="closeStockEditor()" [disabled]="stockSaving()"><span class="sr-only">Close modal</span></button>
    </div>
  } 
  
  @if (variantEditDraft(); as draft) {
    <div class="modal modal-open bg-black/40 backdrop-blur-sm" role="dialog" aria-modal="true">
      <div class="modal-box rounded-2xl p-6 shadow-xl max-w-sm">
        <div class="flex items-center justify-between mb-1">
          <h3 class="text-lg font-black tracking-tight">Edit Settings</h3>
          <button class="btn btn-ghost btn-xs btn-circle bg-base-200" (click)="closeVariantEditor()" [disabled]="variantSaving()">
             <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        <p class="text-xs text-base-content/50 font-medium pb-4 border-b border-base-200">{{ draft.productName }} · {{ draft.variantName }}</p>

        <div class="py-4 space-y-4">
          <p class="text-xs text-base-content/60">Pack size and unit are managed in Product Catalog.</p>
          <div class="flex gap-3">
             <div class="w-1/3">
                <div class="label py-1"><span class="label-text text-xs font-bold text-base-content/60">MRP ₹</span></div>
                <input type="number" class="input input-sm input-bordered w-full opacity-60 bg-base-200" [ngModel]="draft.mrp" disabled/>
             </div>
             <div class="flex-1">
                <div class="label py-1"><span class="label-text text-xs font-bold text-base-content/60">Selling Price ₹</span></div>
                <input type="number" min="0" class="input input-sm input-bordered w-full font-bold text-primary" [disabled]="variantSaving()" [ngModel]="draft.sellingPrice" (ngModelChange)="setVariantSellingPrice($event)"/>
             </div>
          </div>

          <label class="form-control block">
            <span class="label-text font-bold">Maximum units per cart/order</span>
            <input type="number" min="1" step="1" placeholder="No limit" class="input input-bordered w-full"
              [disabled]="variantSaving()" [ngModel]="draft.maxOrderQuantity" (ngModelChange)="setVariantMaxOrderQuantity($event)" />
            <span class="text-xs text-base-content/60">For this variant at this store. Leave blank for no limit. Example: 2 milk packs.</span>
          </label>

          <label class="cursor-pointer flex items-center justify-between p-3 rounded-lg border border-base-200 bg-base-50 hover:bg-base-100 transition-colors mt-2">
             <span class="text-sm font-bold text-base-content/70">Available in store</span>
             <input type="checkbox" class="toggle toggle-sm toggle-success" [disabled]="variantSaving()" [ngModel]="draft.isAvailable" (ngModelChange)="setVariantAvailability($event)"/>
          </label>
        </div>

        <div class="flex gap-2 mt-2">
          <button class="btn btn-sm btn-ghost flex-1 border border-base-200" (click)="closeVariantEditor()" [disabled]="variantSaving()">Cancel</button>
          <button class="btn btn-sm btn-primary flex-1 shadow-sm" (click)="saveVariantEdit()" [disabled]="variantSaving()">
            @if (variantSaving()) { <span class="loading loading-spinner loading-xs"></span> } Apply
          </button>
        </div>
      </div>
      <button class="modal-backdrop" (click)="closeVariantEditor()" [disabled]="variantSaving()"><span class="sr-only">Close modal</span></button>
    </div>
  }

  @if (variantRemoveDraft(); as draft) {
    <div class="modal modal-open bg-black/40 backdrop-blur-sm" role="dialog">
      <div class="modal-box rounded-2xl p-6 max-w-sm text-center">
        <div class="w-16 h-16 rounded-full bg-error/10 text-error flex items-center justify-center text-3xl mx-auto mb-4">
           <i class="fa-solid fa-triangle-exclamation"></i>
        </div>
        <h3 class="text-xl font-black">Remove Map?</h3>
        <p class="text-sm text-base-content/60 mt-2 mx-auto leading-relaxed">
           Remove <strong>{{ draft.productName }}</strong> ({{ draft.variantName }}) from this store? Stock must be zero and the mapping must have no protected inventory or order history. To stop selling while retaining history, turn off Available in store in Edit settings.
        </p>
        @if (variantRemoveError()) {
          <p role="alert" class="text-sm text-error mt-3">{{ variantRemoveError() }}</p>
        }

        <div class="mt-6 flex gap-2">
          <button class="btn btn-ghost flex-1 border border-base-200" (click)="closeRemoveVariantModal()" [disabled]="variantRemoveSaving()">Keep Mapping</button>
          <button class="btn btn-error flex-1" (click)="confirmRemoveVariantMapping()" [disabled]="variantRemoveSaving()">
            @if (variantRemoveSaving()) { <span class="loading loading-spinner loading-xs"></span> } Remove It
          </button>
        </div>
      </div>
      <button class="modal-backdrop" (click)="closeRemoveVariantModal()" [disabled]="variantRemoveSaving()"><span class="sr-only">Close modal</span></button>
    </div>
  }
</section>
